import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const SCROLL_THRESHOLD_PX = 32;
const LAYOUT_GROWTH_SELECTOR = 'canvas, figure, img, svg';

export function isConversationAtBottom(
  container: HTMLElement,
  threshold = SCROLL_THRESHOLD_PX
): boolean {
  return (
    container.scrollHeight - container.scrollTop - container.clientHeight <=
    threshold
  );
}

/**
 * The message the viewport should hold still while the panel reflows, plus the
 * offset it had from the container top when the anchor was taken. Holding an
 * anchor suppresses native scroll anchoring, so the value it replaced travels
 * with the anchor and is restored on release.
 */
type ConversationAnchor = {
  element: HTMLElement;
  offset: number;
  previousOverflowAnchor: string;
};

function anchorTargetAt(
  container: HTMLElement,
  clientY: number
): HTMLElement | null {
  let closest: HTMLElement | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const child of container.children) {
    if (!(child instanceof HTMLElement)) {
      continue;
    }

    const rect = child.getBoundingClientRect();
    if (clientY >= rect.top && clientY <= rect.bottom) {
      return child;
    }

    const distance =
      clientY < rect.top ? rect.top - clientY : clientY - rect.bottom;
    if (distance < closestDistance) {
      closestDistance = distance;
      closest = child;
    }
  }

  return closest;
}

function isLayoutObservationRoot(node: Element, container: Element): boolean {
  return node === container || node.parentElement === container;
}

/**
 * Observe only the scroll container, its message roots, and media that can grow
 * the conversation (`canvas` / `figure` / `img` / `svg`). With `subtree: true`
 * on the mutation observer, Vega inserts many internal `g` / `path` / `rect`
 * nodes — observing those would leave them registered after a chat switch
 * (unobserve only walks layout-growth selectors) and would re-run followLayout
 * on hover size tweaks.
 */
function observeLayoutNodes(
  resizeObserver: ResizeObserver,
  node: Node,
  container: Element
) {
  if (!(node instanceof Element)) {
    return;
  }

  if (
    node.matches(LAYOUT_GROWTH_SELECTOR) ||
    isLayoutObservationRoot(node, container)
  ) {
    resizeObserver.observe(node);
  }

  if (node.matches(LAYOUT_GROWTH_SELECTOR)) {
    return;
  }

  for (const child of node.querySelectorAll(LAYOUT_GROWTH_SELECTOR)) {
    resizeObserver.observe(child);
  }
}

function unobserveLayoutNodes(resizeObserver: ResizeObserver, node: Node) {
  if (!(node instanceof Element)) {
    return;
  }

  resizeObserver.unobserve(node);
  // Descendants stay observed if we only drop the removed parent: a chat
  // switch unmounts the message wrapper, not each chart/image inside it.
  for (const child of node.querySelectorAll(LAYOUT_GROWTH_SELECTOR)) {
    resizeObserver.unobserve(child);
  }
}

export function useConversationScroll(
  containerRef: RefObject<HTMLElement | null>,
  contentKey: string,
  enabled = true
) {
  const stickToBottomRef = useRef(true);
  const ignoringScrollRef = useRef(false);
  const lastScrollHeightRef = useRef(0);
  const lastScrollTopRef = useRef(0);
  const anchorRef = useRef<ConversationAnchor | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const pinToBottom = useCallback((container: HTMLElement) => {
    ignoringScrollRef.current = true;
    container.scrollTop = container.scrollHeight;
    lastScrollHeightRef.current = container.scrollHeight;
    lastScrollTopRef.current = container.scrollTop;
    ignoringScrollRef.current = false;
  }, []);

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    stickToBottomRef.current = true;
    pinToBottom(container);
    setShowScrollToBottom(false);
  }, [containerRef, pinToBottom]);

  const releaseConversationAnchor = useCallback(() => {
    const container = containerRef.current;
    const anchor = anchorRef.current;
    if (container != null && anchor != null) {
      container.style.overflowAnchor = anchor.previousOverflowAnchor;
    }

    anchorRef.current = null;
  }, [containerRef]);

  /**
   * Takes the message nearest `clientY` as the fixed point for the reflows that
   * follow, so dragging a resize handle rewraps the conversation around the
   * spot the pointer grabbed instead of shifting it under the cursor.
   */
  const anchorConversation = useCallback(
    (clientY: number) => {
      releaseConversationAnchor();

      const container = containerRef.current;
      const target =
        container == null ? null : anchorTargetAt(container, clientY);
      if (container == null || target == null) {
        return;
      }

      anchorRef.current = {
        element: target,
        offset:
          target.getBoundingClientRect().top -
          container.getBoundingClientRect().top,
        previousOverflowAnchor: container.style.overflowAnchor,
      };
      // Native anchoring picks its own fixed point and would fight the offset
      // held below; it stays on for every reflow we do not drive ourselves.
      container.style.overflowAnchor = 'none';
    },
    [containerRef, releaseConversationAnchor]
  );

  const holdAnchor = useCallback(() => {
    const container = containerRef.current;
    const anchor = anchorRef.current;
    if (container == null || anchor == null) {
      return;
    }

    if (!container.contains(anchor.element)) {
      releaseConversationAnchor();
      return;
    }

    const drift =
      anchor.element.getBoundingClientRect().top -
      container.getBoundingClientRect().top -
      anchor.offset;
    if (Math.abs(drift) < 1) {
      return;
    }

    ignoringScrollRef.current = true;
    container.scrollTop += drift;
    lastScrollHeightRef.current = container.scrollHeight;
    lastScrollTopRef.current = container.scrollTop;
    ignoringScrollRef.current = false;
  }, [containerRef, releaseConversationAnchor]);

  const followLayout = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (anchorRef.current != null) {
      holdAnchor();
      return;
    }

    if (!stickToBottomRef.current) {
      return;
    }

    pinToBottom(container);
    setShowScrollToBottom((visible) => (visible ? false : visible));
    requestAnimationFrame(() => {
      const node = containerRef.current;
      if (node != null && stickToBottomRef.current && anchorRef.current == null) {
        pinToBottom(node);
      }
    });
  }, [containerRef, holdAnchor, pinToBottom]);

  const captureStickToBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    stickToBottomRef.current = isConversationAtBottom(container);
  }, [containerRef]);

  // A drag cut short by a close or unmount never reaches its release, so drop
  // the anchor here rather than leaving native anchoring switched off.
  useEffect(
    () => releaseConversationAnchor,
    [enabled, releaseConversationAnchor]
  );

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || ignoringScrollRef.current) {
      return;
    }

    const previousHeight = lastScrollHeightRef.current;
    const previousTop = lastScrollTopRef.current;
    const nextHeight = container.scrollHeight;
    const nextTop = container.scrollTop;
    lastScrollHeightRef.current = nextHeight;
    lastScrollTopRef.current = nextTop;

    const heightGrew = nextHeight > previousHeight;
    const topMoved = Math.abs(nextTop - previousTop) > 1;

    if (anchorRef.current != null) {
      holdAnchor();
      return;
    }

    if (stickToBottomRef.current && heightGrew && !topMoved) {
      pinToBottom(container);
      setShowScrollToBottom((visible) => (visible ? false : visible));
      return;
    }

    const atBottom = isConversationAtBottom(container);
    stickToBottomRef.current = atBottom;
    setShowScrollToBottom(!atBottom);
  }, [containerRef, holdAnchor, pinToBottom]);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    lastScrollHeightRef.current = container.scrollHeight;
    lastScrollTopRef.current = container.scrollTop;
    container.addEventListener('scroll', handleScroll, { passive: true });
    followLayout();

    if (!stickToBottomRef.current) {
      setShowScrollToBottom(!isConversationAtBottom(container));
    }

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef, contentKey, enabled, followLayout, handleScroll]);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const container = containerRef.current;
    if (container == null || typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      followLayout();
    });

    observeLayoutNodes(resizeObserver, container, container);
    for (const child of container.children) {
      observeLayoutNodes(resizeObserver, child, container);
    }

    const mutationObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.removedNodes) {
          unobserveLayoutNodes(resizeObserver, node);
        }
        for (const node of record.addedNodes) {
          observeLayoutNodes(resizeObserver, node, container);
        }
      }
      followLayout();
    });
    mutationObserver.observe(container, { childList: true, subtree: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [containerRef, enabled, followLayout]);

  return {
    anchorConversation,
    captureStickToBottom,
    releaseConversationAnchor,
    scrollToBottom,
    showScrollToBottom,
  };
}
